import React, { useEffect, useState, useMemo } from "react";
import "./ServiceBody.css";
import {
  EmailValidation,
  PhoneNumberValidation,
  StringValidation,
} from "../../../Store/validate";
import {
  GetContent,
  getServiceDetails,
  getServiceDetailsmainServiceId,
  getSubServiceL2,
  UserServiceRequest,
} from "../../../Api/Api";
import {
  showToastErrorMsg,
  showToastSuccessMsg,
} from "../../../Store/toastMsg";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";


export default function ServiceRequestModal({
  isOpen,
  onClose,
  subcategory,
  category,
  hasClosedOnce,
}) {

  const [content, setContent] = useState([]);
  const { id } = useParams(); // ✅ GET ID FROM URL
  const fetchServiceContent = async () => {
    try {
      if (!id) return;

      const data = await GetContent("active", id);

      if (data?.statusCode === 200) {
        setContent(data.data);
      } else {
        setContent([]);
      }
    } catch (error) {
      console.error("Service fetch failed:", error);
      setContent([]);
    }
    setLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchServiceContent();
  }, [id]); // ✅ Re-run when ID changes

  const { slug } = useParams();
  const service = content?.[0];
  const state = useSelector((state) => state);
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const [servicesList, setServicesList] = useState([]);
  const [subServiceL1List, setSubServiceL1List] = useState([]);

  useEffect(() => {
    const fetchServices = async () => {
      if (isOpen) {
        const res = await getServiceDetails("active", "mainService");
        if (res.statusCode === 200) {
          // Ensure we are setting an array
          setServicesList(Array.isArray(res.data) ? res.data : []);
        } else {
          showToastErrorMsg("Failed to load services");
        }
      }
    };

    fetchServices();
  }, [isOpen]);

  useEffect(() => {
    const fetchSubServices = async () => {
      const selectedService = servicesList.find(
        (s) => s.title === formData.servicesCategory,
      );
      const mainServiceId = selectedService?._id;

      if (mainServiceId) {
        const res = await getServiceDetailsmainServiceId(mainServiceId);

        if (res.statusCode === 200) {
          // --- CRITICAL FIX START ---
          // Double check if data is an array, otherwise default to empty array
          const data = Array.isArray(res.data) ? res.data : [];
          setSubServiceL1List(data);
          // --- CRITICAL FIX END ---
        } else {
          setSubServiceL1List([]);
        }
      }
    };

    if (formData.servicesCategory) {
      fetchSubServices();
    }
  }, [formData.servicesCategory]);

  const [subServiceL2List, setSubServiceL2List] = useState([]);

  // EFFECT for SubService L2
  useEffect(() => {
    const fetchL2 = async () => {
      // 1. Find Main ID
      const mainObj = servicesList.find(
        (s) => s.title === formData.servicesCategory,
      );
      // 2. Find Sub L1 ID
      const subL1Obj = subServiceL1List.find(
        (s) => s.title === formData.servicesSubcategory,
      );

      if (mainObj?._id && subL1Obj?._id) {
        const res = await getSubServiceL2(mainObj._id, subL1Obj._id);
        if (res.statusCode === 200) {
          setSubServiceL2List(Array.isArray(res.data) ? res.data : []);
        }
      }
    };

    if (formData.servicesSubcategory) {
      fetchL2();
    } else {
      setSubServiceL2List([]); // Reset if parent is cleared
    }
  }, [formData.servicesSubcategory, subServiceL1List, servicesList]);

  /* ---------------- CONVERT SLUG TO TITLE CASE ---------------- */
  const slugToTitle = (text) => {
    if (!text) return "";
    return text
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  /* ---------------- INIT USER DATA ---------------- */
  useEffect(() => {
    window.scrollTo(0, 0);

    if (state?.auth?.isLoggedIn) {
      setFormData((prev) => ({
        ...prev,
        name: state.auth.auth.name || "",
        email: state.auth.auth.email || "",
        mobileNumber: state.auth.auth.mobileNumber || "",
      }));
    }
  }, [state]);

  useEffect(() => {
    if (service && hasClosedOnce) {
      setFormData((prev) => ({
        ...prev,
        servicesCategory: prev.servicesCategory || service?.mainServiceId?.title || "",
        servicesSubcategory: prev.servicesSubcategory || service?.subServiceL1Id?.title || "",
        servicesTitle: prev.servicesTitle || service?.subServiceL2Id?.title || "",
      }));
    }
  }, [service, hasClosedOnce]);

  /* ---------------- HELPERS ---------------- */
  const setValue = (key, value) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: null }));
  };

const validateField = (key, validator, value) => {
  const result = validator(value);

  setErrors((prev) => ({
    ...prev,
    [key]: result?.status ? null : result?.message,
  }));
};

  /* ---------------- SERVICE NAME LOGIC ---------------- */
  const serviceName = useMemo(() => {
    return (
      service?.subServiceL2Id?.title ||
      service?.subServiceL1Id?.title ||
      slugToTitle(slug) ||
      ""
    );
  }, [service, slug]);

  /* ---------------- SUBMIT ---------------- */
  const validateForm = () => {
    let isValid = true;
    const newErrors = {};

    const nameVal = StringValidation(formData.name || "");
    if (!nameVal.status && !state?.auth?.isLoggedIn) { isValid = false; newErrors.name = nameVal.message || "Name is required"; }

    const phoneVal = PhoneNumberValidation(formData.mobileNumber || "");
    if (!phoneVal.status && !state?.auth?.isLoggedIn) { isValid = false; newErrors.mobileNumber = phoneVal.message || "Valid mobile number is required"; }

    const emailVal = EmailValidation(formData.email || "");
    if (!emailVal.status && !state?.auth?.isLoggedIn) { isValid = false; newErrors.email = emailVal.message || "Valid email is required"; }

    if (!formData.servicesCategory) { isValid = false; newErrors.servicesCategory = "Service is required"; }
    if (subServiceL1List?.length > 0 && !formData.servicesSubcategory) { isValid = false; newErrors.servicesSubcategory = "Sub-Category is required"; }
    if (subServiceL2List?.length > 0 && !formData.servicesTitle) { isValid = false; newErrors.servicesTitle = "Service Detail is required"; }
    if (!formData.subject?.trim()) { isValid = false; newErrors.subject = "Requirement is required"; }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    try {



      const payload = {
        subject: formData.subject || "",
        question: formData.question || "",
        type: "service",
        servicesCategory: formData.servicesSubcategory,
        servicesSubcategory: formData.servicesTitle,
        servicesTitle: formData.servicesCategory || "",
        ...(state?.auth?.isLoggedIn
          ? { userId: state.auth.auth._id }
          : {
            name: formData.name,
            email: formData.email,
            mobileNumber: formData.mobileNumber,
          }),
      };

      const res = await UserServiceRequest(payload);

      if (res?.statusCode === 200) {
        showToastSuccessMsg(res.message);
        setFormData({});
        if (onClose) onClose();
      } else {
        showToastErrorMsg(res?.message || "Submission failed");
      }
    } catch {
      showToastErrorMsg("Something went wrong");
    }
    setLoading(false);
  };

  /* ---------------- SEO VALUES ---------------- */
  const title = `${serviceName} | Dalmaf`;
  const description = `Claim your ${serviceName} effortlessly with Dalmaf. Partner with us for expert guidance and maximize your returns.`;

  const url = typeof window !== "undefined" ? window.location.href : "";

  /* ---------------- STRUCTURED DATA ---------------- */
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: serviceName,
    description,
    provider: {
      "@type": "Organization",
      name: "Dalmaf",
      url: typeof window !== "undefined" ? window.location.origin : "",
    },
    areaServed: {
      "@type": "Country",
      name: "India",
    },
  };

  useEffect(() => {
    if (isOpen) {
      // Store scroll position
      const scrollY = window.scrollY;

      // Lock scroll
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
    } else {
      // Restore scroll
      const scrollY = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";

      window.scrollTo(0, parseInt(scrollY || "0") * -1);
    }

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
    };
  }, [isOpen]);
  if (!isOpen) return null;

  return (
    <>
      <div
        className="modal d-block"
        style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">REQUEST A CALL BACK</h5>
              {!hasClosedOnce && (
                <button
                  type="button"
                  className="btn-close"
                  onClick={onClose}
                ></button>
              )}
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-3">
                  <input
                    className="form-control form-control-lg"
                    placeholder="Name *"
                    value={formData.name || ""}
                    disabled={!!state?.auth?.auth?.name}
                    onChange={(e) => setValue("name", e.target.value)}
                    onBlur={(e) =>
                      validateField("name", StringValidation, e.target.value)
                    }
                  />
                  {errors.name && <div className="text-danger small mt-1 text-start">{errors.name}</div>}
                </div>

                <div className="mb-3">
                  <input
                    className="form-control form-control-lg"
                    placeholder="Mobile *"
                    maxLength={10}
                    value={formData.mobileNumber || ""}
                    disabled={state?.auth?.auth?.mobileNumber}
                    onChange={(e) => setValue("mobileNumber", e.target.value)}
                    onBlur={(e) =>
                      validateField(
                        "mobileNumber",
                        PhoneNumberValidation,
                        e.target.value,
                      )
                    }
                  />
                  {errors.mobileNumber && <div className="text-danger small mt-1 text-start">{errors.mobileNumber}</div>}
                </div>

                <div className="mb-3">
                  <input
                    type="email"
                    className="form-control form-control-lg"
                    placeholder="Email *"
                    value={formData.email || ""}
                    disabled={state?.auth?.auth?.email}
                    onChange={(e) => setValue("email", e.target.value)}
                    onBlur={(e) =>
                      validateField("email", EmailValidation, e.target.value)
                    }
                  />
                  {errors.email && <div className="text-danger small mt-1 text-start">{errors.email}</div>}
                </div>

                <div className="mb-3">
                  <select
                    className="form-select form-select-lg"
                    value={formData.servicesCategory || ""}
                    onChange={(e) =>
                      setValue("servicesCategory", e.target.value)
                    }
                  >
                    <option value="" disabled>Select a Service *</option>
                    {servicesList && servicesList.length > 0 ? (
                      servicesList.map((service) => (
                        <option key={service._id} value={service.title}>
                          {service.title}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading services...</option>
                    )}
                  </select>
                  {errors.servicesCategory && (
                    <div className="text-danger small mt-1 text-start">
                      {errors.servicesCategory}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <select
                    className="form-select form-select-lg"
                    value={formData.servicesSubcategory || ""}
                    disabled={!subServiceL1List.length}
                    onChange={(e) =>
                      setValue("servicesSubcategory", e.target.value)
                    }
                  >
                    <option value="">Select Sub-Category {subServiceL1List.length ? "*" : ""}</option>
                    {subServiceL1List?.map((sub) => (
                      <option key={sub._id} value={sub.title}>
                        {sub.title}
                      </option>
                    ))}
                  </select>
                  {errors.servicesSubcategory && (
                    <div className="text-danger small mt-1 text-start">
                      {errors.servicesSubcategory}
                    </div>
                  )}
                </div>

                <div className="mb-3">
                  <select
                    className="form-select form-select-lg"
                    value={formData.servicesTitle || ""}
                    disabled={!subServiceL2List.length}
                    onChange={(e) => setValue("servicesTitle", e.target.value)}
                  >
                    <option value="">Select Service Detail {subServiceL2List.length ? "*" : ""}</option>
                    {subServiceL2List.map((item) => (
                      <option key={item._id} value={item.title}>
                        {item.title}
                      </option>
                    ))}
                  </select>
                  {errors.servicesTitle && (
                    <div className="text-danger small mt-1 text-start">
                      {errors.servicesTitle}
                    </div>
                  )}
                </div>

                <div className="mb-4">
                  <textarea
                    rows="3"
                    className="form-control form-control-lg"
                    placeholder="Please let us know your requirements *"
                    value={formData.subject || ""}
                    onChange={(e) => setValue("subject", e.target.value)}
                  />
                  {errors.subject && <div className="text-danger small mt-1 text-start">{errors.subject}</div>}
                </div>

                <button className="btn btn-primary btn-lg w-100 fw-bold" disabled={loading} style={{ borderRadius: '8px', padding: '12px' }}>
                  {loading ? "Submitting..." : "Get Started"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
